import { Coords, Mote } from '@motes-sdk';
import { Position } from 'geojson';

export interface SiblingStories { story: Mote, previousStory: Mote, nextStory: Mote };

export enum MapEditorEventType {
    SET_MAP_SEARCH = "SET_MAP_SEARCH",
    STORY_CREATED = "STORY_CREATED",
    STORY_DELETED = "STORY_DELETED",
    STORY_ORDER_CHANGED = "STORY_ORDER_CHANGED",
    STORY_DRAG_END = "STORY_DRAG_END",
    STORY_DRAG_START = "STORY_DRAG_START",
    MOVE_STORY = "MOVE_STORY",
    WAYPOINT_CLICKED = "WAYPOINT_CLICKED",
    WAYPOINT_DELETED = "WAYPOINT_DELETED",
    WAYPOINT_MOVED = "WAYPOINT_MOVED",
}

/**
 * Map editor events used to distribute user interactions from the map editor
 * to other components and events to the map editor.
 */
interface MapEditorEventInterface {
    readonly type: MapEditorEventType;
}

export class SetMapSearchEvent implements MapEditorEventInterface {
    public readonly type = MapEditorEventType.SET_MAP_SEARCH;
    public searchText: string;
    public coordinates: Coords;
    public openSearchBar: boolean;

    /**
     * @param searchText Place, address or POI text to search for
     */
    constructor(searchText: string, openSearchBar?: boolean);

    /**
     * @param coordinates Optional coordinates to show search result for
     */
    constructor(coordinates: Coords, openSearchBar?: boolean);

    /**
     * @param searchTextOrCoordinates Text denoting a place, address, POI or a coordinate set
     */
    constructor(searchTextOrCoordinates: string | Coords, openSearchBar: boolean = true) {
        this.searchText = (typeof(searchTextOrCoordinates) == 'string') ? searchTextOrCoordinates : '';
        this.coordinates = (searchTextOrCoordinates instanceof Coords) ? searchTextOrCoordinates : null;
        this.openSearchBar = openSearchBar;
    }
}

export class StoryDragStartEvent implements MapEditorEventInterface {
    public readonly type = MapEditorEventType.STORY_DRAG_START;

    /**
     * @param storyKey Key of the story that is being moved
     */
     constructor(public storyKey: string) {
    }
}

export class StoryDragEndEvent implements MapEditorEventInterface {
    public readonly type = MapEditorEventType.STORY_DRAG_END;

    /**
     * @param storyKey Key of the story that has been moved
     * @param oldPosition The position from which the story was moved
     * @param newPosition The position that the story was moved to
     */
     constructor(public storyKey: string, public oldPosition: Coords, public newPosition: Coords) {
    }
}

export class WaypointClickedEvent implements MapEditorEventInterface {
    public readonly type = MapEditorEventType.WAYPOINT_CLICKED;

    /**
     * @param id ID of the waypoint clicked
     * @param position Position of the waypoint clicked
     * @param isSnapped Whether waypoint is snapped to route or not
     * @param storyKey Key of the story containing the waypoint
     */
    constructor(public id: string, public position: Position, public isSnapped: boolean, public storyKey: string) {
    }
}

export class WaypointMovedEvent implements MapEditorEventInterface {
    public readonly type = MapEditorEventType.WAYPOINT_MOVED;

    /**
     * @param id ID of the waypoint moved
     * @param position Position of the waypoint moved
     * @param isSnapped Whether waypoint is snapped to route or not
     * @param storyKey Key of the story containing the waypoint
     */
    constructor(public id: string, public position: Position, public isSnapped: boolean, public storyKey: string) {
    }
}

export class WaypointDeletedEvent implements MapEditorEventInterface {
    public readonly type = MapEditorEventType.WAYPOINT_DELETED;

    /**
     * @param id Id of the waypoint deleted
     */
    constructor(public id: string) {
    }
}

export class MoveStoryEvent implements MapEditorEventInterface {
    public readonly type = MapEditorEventType.MOVE_STORY;

    /**
     * @param storyKey Key of the story to move
     * @param newPosition The position to move the story to
     */
     constructor(public storyKey: string, public newPosition: Coords) {
    }
}

export class StoryCreatedEvent implements MapEditorEventInterface {
    public readonly type = MapEditorEventType.STORY_CREATED;

    /**
     * @param story Instance of a newly created story
     * @param previousStory Instance of the previousStory (connected sibling) when the story was created (if any)
     */
    constructor(public story: Mote, public previousStory: Mote) {
    }
}

export class StoryDeletedEvent implements MapEditorEventInterface {
    public readonly type = MapEditorEventType.STORY_DELETED;

    /**
     * @param storyKey Key of the story that has been deleted
     */
    constructor(public storyKey: string) {
    }
}

export class StoryOrderChanged implements MapEditorEventInterface {
    public readonly type = MapEditorEventType.STORY_ORDER_CHANGED;

    /**
     * @param storyKey Key of the story that has been deleted
     */
    constructor(public storiesSwapped: { story: Mote, previousStory: Mote, nextStory: Mote }[]) {
    }
}

export type MapEditorEvent = SetMapSearchEvent | StoryCreatedEvent | StoryDeletedEvent | StoryOrderChanged | StoryDragEndEvent | StoryDragStartEvent | WaypointMovedEvent| MoveStoryEvent | WaypointClickedEvent | WaypointDeletedEvent;
